/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "emarsys",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "OpenID Connect",
          "depth": 1,
          "id": "openid-connect"
        }, {
          "value": "WSSE",
          "depth": 1,
          "id": "wsse"
        }, {
          "value": "Endpoint permissions",
          "depth": 1,
          "id": "endpoint-permissions"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Fields mapping",
          "depth": 1,
          "id": "fields-mapping"
        }, {
          "value": "Debugging",
          "depth": 0,
          "id": "debugging"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      strong: "strong",
      h3: "h3",
      code: "code",
      img: "img",
      h4: "h4",
      a: "a",
      em: "em"
    }, _provideComponents(), props.components), {PremiumDestination, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Emarsys"
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports two ways to authenticate with Emarsys:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "OpenID Connect"
          }), " (recommended): uses a client ID and client secret with the Emarsys v3 API."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "WSSE"
          }), ": uses a username and API secret with the Emarsys v2 API. Emarsys is retiring WSSE authentication. It stops supporting WSSE on February 1, 2027, and may turn off WSSE-based APIs at any time after that date."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If your Emarsys destination uses WSSE, switch it to OpenID Connect before\nFebruary 1, 2027. Create OpenID Connect credentials as described below, then\nopen your destination's ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab, select ", _jsx(_components.strong, {
            children: "Use OpenID Connect\n(recommended)"
          }), ", enter the client ID and client secret, and save. Your\nexisting syncs keep working without any other changes."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "openid-connect",
        children: "OpenID Connect"
      }), "\n", _jsxs(_components.p, {
        children: ["An Emarsys account owner can create OpenID Connect API credentials under ", _jsx(_components.strong, {
          children: "Management"
        }), " > ", _jsx(_components.strong, {
          children: "Security Settings"
        }), ". Copy the client ID and client secret into Hightouch."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Emarsys only displays the client secret once, when you create the credentials.\nSave it before you leave the page."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["OpenID Connect credentials always use the default Emarsys API host, ", _jsx(_components.code, {
          children: "api.emarsys.net"
        }), ", even if your account is on a regional instance."]
      }), "\n", _jsx(_components.h3, {
        id: "wsse",
        children: "WSSE"
      }), "\n", _jsxs(_components.p, {
        children: ["You will need your username, usually in the format of ", _jsx(_components.code, {
          children: "account_name00X"
        }), " where ", _jsx(_components.code, {
          children: "X"
        }), " is a digit, and the API secret."]
      }), "\n", _jsxs(_components.p, {
        children: ["To get the API secret, you can create a new API user for Hightouch under ", _jsx(_components.strong, {
          children: "Management"
        }), " > ", _jsx(_components.strong, {
          children: "Security Settings"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-api-settings.png",
          alt: ""
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Make sure to copy and save the secret key here since the entire key will no\nlonger display after you close the confirmation dialog box."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If your Emarsys account is on a regional instance, enter its API host (for example, ", _jsx(_components.code, {
          children: "suite6.emarsys.net"
        }), ") in the ", _jsx(_components.strong, {
          children: "API host"
        }), " field. Leave it blank to use the default, ", _jsx(_components.code, {
          children: "api.emarsys.net"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "endpoint-permissions",
        children: "Endpoint permissions"
      }), "\n", _jsx(_components.p, {
        children: "Whichever authentication method you use, you need to enable the necessary endpoints for your API credentials. The endpoints\nHightouch requires depend on how you configure your syncs."
      }), "\n", _jsx(_components.h4, {
        id: "always-required",
        children: "Always required"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/core-api-reference/a0l7f9tviiuiv-list-available-fields",
            children: _jsx(_components.code, {
              children: "field.get"
            })
          }), " — Used during the connection test and to load available contact fields for mapping. Without this endpoint enabled, the connection test will fail with a 403 error."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "contact-syncs",
        children: "Contact syncs"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/core-api-reference/g617t4kfs4y69-create-contact",
            children: _jsx(_components.code, {
              children: "contact.create"
            })
          }), " — Required for upsert sync mode (creates new contacts)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/core-api-reference/f8ljhut3ac2i1-update-contacts",
            children: _jsx(_components.code, {
              children: "contact.update"
            })
          }), " — Required for upsert and update sync modes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/core-api-reference/szmq945esac90-delete-contacts",
            children: _jsx(_components.code, {
              children: "contact.delete"
            })
          }), " — Only required if you plan to remove contacts when data leave your warehouse."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "contact-list-syncs",
        children: "Contact list syncs"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/core-api-reference/paz2wputeebqx-list-contact-lists",
            children: _jsx(_components.code, {
              children: "contactlist.list"
            })
          }), " — Required to load available contact lists for selection."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/core-api-reference/1m0m70hy3tuov-add-contacts-to-a-contact-list",
            children: _jsx(_components.code, {
              children: "contactlist.contact.add"
            })
          }), " — Required to add contacts to a list."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/emarsys-api/b3A6MjQ4OTk4MzI-remove-contacts-from-a-contact-list",
            children: _jsx(_components.code, {
              children: "contactlist.contact.delete"
            })
          }), " — Only required if you plan to remove contacts from a list."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/emarsys-api/b3A6MjQ4OTk4MjI-create-a-contact-list",
            children: _jsx(_components.code, {
              children: "contactlist.create"
            })
          }), " — Only required if you plan to create new contact lists from Hightouch."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The following screenshot shows an example of a working permissions configuration in the Emarsys UI.\nIt is also safe to enable all ", _jsx(_components.strong, {
          children: "contacts"
        }), ", ", _jsx(_components.strong, {
          children: "fields"
        }), ", and ", _jsx(_components.strong, {
          children: "contact lists"
        }), " endpoints."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/emarsys/emarsys-permissions.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["API users and permissions are described in the ", _jsx(_components.a, {
          href: "https://dev.emarsys.com/docs/core-api-reference/ef41493bd7812-endpoint-permission-settings",
          children: "Emarsys docs"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing ", _jsx(_components.strong, {
          children: "contacts"
        }), " and ", _jsx(_components.strong, {
          children: "contact lists"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "Here are the possible modes for the Emarsys contacts:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new contacts to Emarsys and updates existing contacts with the fields that change in your warehouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": updates particular fields on existing contacts in Emarsys. It doesn't add new objects."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This integration also supports ", _jsx(_components.strong, {
          children: "Segment"
        }), " mode. New users will be inserted into the list.\nUsers that are removed from the model will be removed from the list."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-segment-mode.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to Emarsys by ", _jsx(_components.em, {
          children: "any"
        }), " default and custom fields. However, it's important to make sure\nthat the field you are using is unique to ensure the best results. For example, Emarsys will throw an error if it detects duplicate\nvalues in an attempt to update or remove a batch of contacts.\nWe recommend using ", _jsx(_components.code, {
          children: "email"
        }), " or creating a custom unique field, like an ", _jsx(_components.code, {
          children: "external_id"
        }), " (make sure to set it as ", _jsx(_components.em, {
          children: "unique"
        }), ")."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-record-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "fields-mapping",
        children: "Fields mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can also map to any updatable fields in Emarsys, including your custom fields."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-fields.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "However, there are some things to keep in mind:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The Emarsys API uses the field's ID when making requests so if you need to use the JSON editor, you will not see any named fields.\nInstead, you will only see the field ids. You can use this ", _jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/emarsys-api/ZG9jOjI0ODk5NzY3-contact-system-fields",
            children: "table"
          }), " to cross reference the field ids.\nYou can also view your settings to reference your custom fields."]
        }), "\n", _jsxs(_components.li, {
          children: ["Any fields with a date type are formatted: ", _jsx(_components.em, {
            children: "YYYY-MM-DD"
          }), ". You can have your date values be a string in this format (for example, \"2020-11-30\")."]
        }), "\n", _jsxs(_components.li, {
          children: ["You must use the Emarsys accepted values for any choice fields (that is, ", _jsx(_components.code, {
            children: "gender"
          }), ", ", _jsx(_components.code, {
            children: "title"
          }), ", etc.)."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "debugging",
        children: "Debugging"
      }), "\n", _jsxs(_components.p, {
        children: ["Similarly to the fields mapping in the sync configuration, the request will only show the fields' ids unless it's Emarsys' internal ", _jsx(_components.code, {
          children: "id"
        }), " and ", _jsx(_components.code, {
          children: "uid"
        }), ".\nPlease refer to this ", _jsx(_components.a, {
          href: "https://dev.emarsys.com/docs/emarsys-api/ZG9jOjI0ODk5NzY3-contact-system-fields",
          children: "table"
        }), " and your custom fields settings to cross reference\nthe ID to the field's name."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-request.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The Emarsys API lets us upsert/update/remove contacts in batches per API call. Most cases, the API will let us know which specific rows fail, instead of failing\nthe entire batch. However, the error message may not be very descriptive but it will reference their ", _jsx(_components.a, {
          href: "https://dev.emarsys.com/docs/emarsys-api/ZG9jOjI0ODk5NzY4-http-200-errors",
          children: "internal error code"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
